import React from 'react';
import './App.css';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import ReactLoading from 'react-loading';
import Box from '@mui/material/Box';
import AccountSettings from './pages/AccountSettings';
import { AuthContext } from './context/AuthContext';
import Home from './pages/Home';
import { AuthProps } from './types/props';
import Subscriptions from './pages/Subscriptions';
import FeaturesPricing from './pages/FeaturesPricing';
import NOTFOUND from './pages/NotFound';
import HeaderBanner from './pages/components/HeaderBanner';
import Login from './pages/Login';
import Auth from './pages/Auth';
import ForgotPassword from './pages/ForgotPassword';
import MyServices from './pages/ProfilePage/MyServices';
import Dashboard from './pages/Dashboard';
import GalleryImages from './pages/ProfilePage/GalleryImages';
import MyBusinessHours from './pages/ProfilePage/MyBusinessHours';
import ShopBarber from './pages/ProfilePage/ShopBarber';
import SideNav from './pages/components/SideNav';
import Profile from './pages/ProfilePage/Profile';
import Footer from './pages/components/DefaultFooter';
import Amenities from './pages/ProfilePage/Amenities';
import ProfileDataProvider from './context/ProfileDataContext';
import ScheduleManager from './pages/ScheduleManager';
import ReservationManager from './pages/ReservationManager';
import ReservationConfirmation from './pages/ReservationConfirmation';
import GetStarted from './pages/Registration/GetStarted';
import ShopOwnerRegistration from './pages/Registration/ShopOwnerRegistration';
import ShopRegistration from './pages/Registration/ShopRegistration';
import ShopBarberRegistration from './pages/Registration/ShopBarberRegistration';
import BarberRegistration from './pages/Registration/BarberRegistration';
import SubscriptionSelection from './pages/Registration/SubscriptionSelection';
import FindBarbers from './pages/FindBarbers';
import PaymentReturn from './pages/PaymentReturn';
import BarberBookingCalendar from './pages/BarberBookingCalendar';
import ShopBookingCalendar from './pages/ShopBookingCalendar';
import PublicBarberProfile from './pages/PublicBarberProfile';
import PublicShopProfile from './pages/PublicShopProfile';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import HelpSupport from './pages/HelpSupport';
import AnalyticsListener from './AnalyticsListener';
import { useHydratedSession } from './hooks/useHydratedSession';
import AuthNoticeRedirect from './pages/components/AuthNoticeRedirect';

function App({ domain, clientId }: AuthProps) {
  // useHydratedSession wraps useAuth0() and additionally makes sure the
  // app's own session (sessionStorage `user`/`usertoken`) is hydrated
  // before protected routes render — not just that Auth0 itself reports
  // isAuthenticated. sessionStorage is tab-scoped, so a fresh tab that
  // inherits a still-valid (localStorage-cached) Auth0 token without
  // visiting /authenticate would otherwise mount routed components with
  // no app session and issue requests with empty/stale identity.
  const { isAuthenticated, isLoading, ready, error, retry } =
    useHydratedSession();

  return (
    <div>
      <Router>
        <AnalyticsListener />
        <AuthNoticeRedirect />
        <HeaderBanner />
        {error && isAuthenticated ? (
          // A transient failure (network/5xx) resolving the signed-in
          // user's partner/role data — not evidence of a role mismatch,
          // so the Auth0 session was left intact. Offer a retry instead
          // of either logging the user out or leaving them on an
          // indefinite spinner.
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '100vh',
              gap: 2,
              textAlign: 'center',
              px: 2,
            }}
          >
            <Box component="p" sx={{ fontSize: '18px' }}>
              Something went wrong loading your account. Please check your
              connection and try again.
            </Box>
            <button onClick={retry}>Retry</button>
          </Box>
        ) : isLoading || (isAuthenticated && !ready) ? (
          // Don't decide route access (and don't fall through to the
          // `*` NOTFOUND route) until Auth0 has resolved whether there's
          // an existing session, AND (if authenticated) the app's own
          // session has been hydrated — otherwise a refresh, or a fresh
          // tab that inherits a cached Auth0 session, would briefly
          // render protected routes as not-found or with no identity
          // before hydration settles.
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '100vh',
            }}
          >
            <ReactLoading
              type={'spin'}
              color={'#dd8709'}
              height={40}
              width={40}
            />
          </Box>
        ) : (
          <Routes>
            <Route path={'/'} element={<Home />} />
            <Route path={'*'} element={<NOTFOUND />} />
            <Route path={'/subscriptions'} element={<Subscriptions />} />
            <Route path={'/features-pricing'} element={<FeaturesPricing />} />
            <Route path={'/privacy-policy'} element={<PrivacyPolicy />} />
            <Route
              path={'/terms-conditions'}
              element={<TermsAndConditions />}
            />
            <Route path={'/help-support'} element={<HelpSupport />} />
            <Route path={'/user-login'} element={<Login />} />
            <Route path={'/iforgot'} element={<ForgotPassword />} />
            <Route path={'/authenticate'} element={<Auth />} />
            <Route
              path={'/confirm-reservation'}
              element={<ReservationConfirmation />}
            />
            {/* New Registration Flow */}
            <Route path={'/get-started'} element={<GetStarted />} />
            <Route
              path={'/register/shop-owner'}
              element={<ShopOwnerRegistration />}
            />
            <Route path={'/register/shop'} element={<ShopRegistration />} />
            <Route
              path={'/register/shop-barbers'}
              element={<ShopBarberRegistration />}
            />
            <Route path={'/register/barber'} element={<BarberRegistration />} />
            <Route
              path={'/register/subscription'}
              element={<SubscriptionSelection />}
            />
            <Route path={'/find-barbers'} element={<FindBarbers />} />
            <Route path={'/return'} element={<PaymentReturn />} />
            <Route path={'/payment-return'} element={<PaymentReturn />} />
            <Route
              path={'/barber/:barberId/calendar'}
              element={<BarberBookingCalendar />}
            />
            <Route
              path={'/shop/:shopId/calendar'}
              element={<ShopBookingCalendar />}
            />
            <Route
              path={'/barber/:barberId'}
              element={<PublicBarberProfile />}
            />
            <Route path={'/shop/:shopId'} element={<PublicShopProfile />} />

            {isAuthenticated && ready && (
              <>
                <Route
                  path={'/account-settings'}
                  element={
                    <AuthContext.Provider value={{ domain, clientId }}>
                      <div
                        className="layout"
                        style={{
                          height: '100vh',
                          display: 'grid',
                          gridTemplateColumns: '280px 1fr',
                          gridRow: '100%',
                        }}
                      >
                        <div
                          className="sideBar"
                          style={{ gridColumn: '1 / 2', gridRow: '1 / 3' }}
                        >
                          <SideNav />
                        </div>
                        <div
                          className="main"
                          style={{
                            gridColumn: '2 / 5',
                            height: '100vh',
                            padding: '0px 20px',
                            display: 'flex',
                            flexDirection: 'column',
                            overflowY: 'auto',
                          }}
                        >
                          <div style={{ flex: '1 0 auto' }}>
                            <AccountSettings />
                          </div>
                          <Footer />
                        </div>
                      </div>
                    </AuthContext.Provider>
                  }
                />
                <Route
                  path={'/subscription-details'}
                  element={
                    <AuthContext.Provider value={{ domain, clientId }}>
                      <div
                        className="layout"
                        style={{
                          height: '100%',
                          display: 'grid',
                          gridTemplateColumns: '280px 1fr',
                          gridRow: '100%',
                        }}
                      >
                        <div
                          className="sideBar"
                          style={{ gridColumn: '1 / 2', gridRow: '1 / 3' }}
                        >
                          <SideNav />
                        </div>
                        <div
                          className="main"
                          style={{
                            gridColumn: '2 / 5',
                            height: '100vh',
                            padding: '0px 20px',
                          }}
                        >
                          <Subscriptions />
                        </div>
                      </div>
                    </AuthContext.Provider>
                  }
                />
                <Route
                  path={'/services'}
                  element={
                    <AuthContext.Provider value={{ domain, clientId }}>
                      <div
                        className="layout"
                        style={{
                          height: '100%',
                          display: 'grid',
                          gridTemplateColumns: '280px 1fr',
                          gridRow: '100%',
                        }}
                      >
                        <div
                          className="sideBar"
                          style={{ gridColumn: '1 / 2', gridRow: '1 / 3' }}
                        >
                          <SideNav />
                        </div>
                        <div
                          className="main"
                          style={{
                            gridColumn: '2 / 5',
                            height: '100vh',
                            padding: '120px 30px',
                          }}
                        >
                          <div style={{ margin: '0' }}>
                            <ProfileDataProvider>
                              <Profile />
                              <MyServices />
                            </ProfileDataProvider>
                          </div>
                        </div>
                      </div>
                    </AuthContext.Provider>
                  }
                />
                <Route
                  path={'/dashboard'}
                  element={
                    <AuthContext.Provider value={{ domain, clientId }}>
                      <div
                        className="layout"
                        style={{
                          height: '100vh',
                          display: 'grid',
                          gridTemplateColumns: '280px 1fr',
                          gridRow: '100%',
                        }}
                      >
                        <div
                          className="sideBar"
                          style={{ gridColumn: '1 / 2', gridRow: '1 / 3' }}
                        >
                          <SideNav />
                        </div>
                        <div
                          className="mainDasboard"
                          style={{
                            gridColumn: '2 / 5',
                            height: '100vh',
                            padding: '10px 10px',
                            display: 'flex',
                            flexDirection: 'column',
                            overflowY: 'auto',
                          }}
                        >
                          <div
                            style={{ flex: '1 0 auto', marginBottom: '10px' }}
                          >
                            <Dashboard />
                          </div>
                          <Footer />
                        </div>
                      </div>
                    </AuthContext.Provider>
                  }
                />
                <Route
                  path={'/reservation-manager'}
                  element={
                    <AuthContext.Provider value={{ domain, clientId }}>
                      <div
                        className="layout"
                        style={{
                          height: '100vh',
                          display: 'grid',
                          gridTemplateColumns: '280px 1fr',
                          gridRow: '100%',
                        }}
                      >
                        <div
                          className="sideBar"
                          style={{ gridColumn: '1 / 2', gridRow: '1 / 3' }}
                        >
                          <SideNav />
                        </div>
                        <div
                          className="main"
                          style={{
                            gridColumn: '2 / 5',
                            height: '100vh',
                            padding: '10px 20px',
                            display: 'flex',
                            flexDirection: 'column',
                            overflowY: 'auto',
                          }}
                        >
                          <div style={{ flex: '1 0 auto' }}>
                            <ReservationManager />
                          </div>
                          <Footer />
                        </div>
                      </div>
                    </AuthContext.Provider>
                  }
                />
                <Route
                  path={'/calendar-manager'}
                  element={
                    <AuthContext.Provider value={{ domain, clientId }}>
                      <div
                        className="layout"
                        style={{
                          height: '100vh',
                          display: 'grid',
                          gridTemplateColumns: '280px 1fr',
                          gridRow: '100%',
                        }}
                      >
                        <div
                          className="sideBar"
                          style={{ gridColumn: '1 / 2', gridRow: '1 / 3' }}
                        >
                          <SideNav />
                        </div>
                        <div
                          className="main"
                          style={{
                            gridColumn: '2 / 5',
                            height: '100vh',
                            padding: '10px 20px',
                            display: 'flex',
                            flexDirection: 'column',
                            overflowY: 'auto',
                          }}
                        >
                          <div style={{ flex: '1 0 auto' }}>
                            <ScheduleManager />
                          </div>
                          <Footer />
                        </div>
                      </div>
                    </AuthContext.Provider>
                  }
                />
                <Route
                  path={'/my-gallery'}
                  element={
                    <AuthContext.Provider value={{ domain, clientId }}>
                      <div
                        className="layout"
                        style={{
                          height: '100vh',
                          display: 'grid',
                          gridTemplateColumns: '280px 1fr',
                          gridRow: '100%',
                        }}
                      >
                        <div
                          className="sideBar"
                          style={{ gridColumn: '1 / 2', gridRow: '1 / 3' }}
                        >
                          <SideNav />
                        </div>
                        <div
                          className="main"
                          style={{
                            gridColumn: '2 / 5',
                            height: '100vh',
                            padding: '120px 30px',
                          }}
                        >
                          <ProfileDataProvider>
                            <Profile />
                            <GalleryImages />
                          </ProfileDataProvider>
                        </div>
                      </div>
                    </AuthContext.Provider>
                  }
                />
                <Route
                  path={'/business-hours'}
                  element={
                    <AuthContext.Provider value={{ domain, clientId }}>
                      <div
                        className="layout"
                        style={{
                          height: '100vh',
                          display: 'grid',
                          gridTemplateColumns: '280px 1fr',
                          gridRow: '100%',
                        }}
                      >
                        <div
                          className="sideBar"
                          style={{ gridColumn: '1 / 2', gridRow: '1 / 3' }}
                        >
                          <SideNav />
                        </div>
                        <div
                          className="main"
                          style={{
                            gridColumn: '2 / 5',
                            height: '100vh',
                            padding: '120px 30px',
                          }}
                        >
                          <ProfileDataProvider>
                            <Profile />
                            <MyBusinessHours />
                          </ProfileDataProvider>
                        </div>
                      </div>
                    </AuthContext.Provider>
                  }
                />
                <Route
                  path={'/barbers'}
                  element={
                    <AuthContext.Provider value={{ domain, clientId }}>
                      <div
                        className="layout"
                        style={{
                          height: '100vh',
                          display: 'grid',
                          gridTemplateColumns: '280px 1fr',
                          gridRow: '100%',
                        }}
                      >
                        <div
                          className="sideBar"
                          style={{ gridColumn: '1 / 2', gridRow: '1 / 3' }}
                        >
                          <SideNav />
                        </div>
                        <div
                          className="main"
                          style={{
                            gridColumn: '2 / 5',
                            height: '100vh',
                            padding: '120px 30px',
                          }}
                        >
                          <ProfileDataProvider>
                            <Profile />
                            <ShopBarber />
                          </ProfileDataProvider>
                        </div>
                      </div>
                    </AuthContext.Provider>
                  }
                />
                <Route
                  path={'/amenities'}
                  element={
                    <AuthContext.Provider value={{ domain, clientId }}>
                      <div
                        className="layout"
                        style={{
                          height: '100vh',
                          display: 'grid',
                          gridTemplateColumns: '280px 1fr',
                          gridRow: '100%',
                        }}
                      >
                        <div
                          className="sideBar"
                          style={{ gridColumn: '1 / 2', gridRow: '1 / 3' }}
                        >
                          <SideNav />
                        </div>
                        <div
                          className="main"
                          style={{
                            gridColumn: '2 / 5',
                            height: '100vh',
                            padding: '120px 30px',
                          }}
                        >
                          <ProfileDataProvider>
                            <Profile />
                            <Amenities />
                          </ProfileDataProvider>
                        </div>
                      </div>
                    </AuthContext.Provider>
                  }
                />
              </>
            )}
          </Routes>
        )}
      </Router>
    </div>
  );
}

export default App;
